import React, { useMemo, useRef, useState } from "react";
import { CrewPilotModuleShell } from "../CrewPilotModuleShell";
import { PAGE_LAYOUT } from "../../layout/pageLayout";
import { DeskEmpty, DeskPanel, DeskPill, DeskSearch, DeskTable } from "../desktop/DeskTable";
import { formatLogDuration } from "../timelogs/timeLogModel";
import { money } from "../invoice/invoiceDesktopParts";
import "../shared/desktopKpiStrip.css";

const INITIAL_VISIBLE = 8;

function rateLabel(row) {
  const rate = Number(row.hourly_rate);
  return row.hourly_rate != null && row.hourly_rate !== "" && Number.isFinite(rate)
    ? `${money(rate)}/hr`
    : "—";
}

/**
 * Desktop Accounts Payable. The unpaid entries are the workspace: KPI strip
 * on top (the Unpaid Entries tile focuses the list), then the entries table,
 * with Payroll Liability as a compact secondary panel. Values are exactly
 * those the screen already loads — no new metrics or calculations.
 */
export function AccountsPayableDesktop({
  onBack,
  unpaidEntries,
  payrollLiability,
  crewLiability,
  unpaidRows = [],
}) {
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState(false);
  const sectionRef = useRef(null);

  const term = search.trim().toLowerCase();
  const matches = useMemo(
    () => (unpaidRows || []).filter((row) => !term || String(row.employee || "").toLowerCase().includes(term)),
    [unpaidRows, term],
  );
  const showAll = expanded || Boolean(term);
  const visible = showAll ? matches : matches.slice(0, INITIAL_VISIBLE);
  const hiddenCount = matches.length - visible.length;

  const focusEntries = () => {
    setExpanded(true);
    const node = sectionRef.current;
    if (node) {
      node.scrollIntoView?.({ behavior: "smooth", block: "start" });
      node.focus?.({ preventScroll: true });
    }
  };

  const columns = [
    {
      key: "employee",
      label: "Employee",
      width: "40%",
      render: (row) => <span className="dt-strong">{row.employee || "Unknown Employee"}</span>,
    },
    {
      key: "hours",
      label: "Hours",
      align: "right",
      width: "20%",
      render: (row) => <span className="dt-num">{formatLogDuration(row)}</span>,
    },
    {
      key: "rate",
      label: "Hourly Rate",
      align: "right",
      width: "20%",
      render: (row) => <span className="dt-num">{rateLabel(row)}</span>,
    },
    {
      key: "status",
      label: "Status",
      width: "20%",
      render: () => <DeskPill tone="gold">Unpaid</DeskPill>,
    },
  ];

  const liabilityRows = crewLiability || [];

  return (
    <CrewPilotModuleShell
      title="Accounts Payable"
      subtitle="Financial Operations Center"
      layout={PAGE_LAYOUT.COMMAND}
      onBack={onBack}
      primary
      compact
      headerActions={
        <div className="cdh-actions">
          <DeskSearch
            value={search}
            onChange={setSearch}
            placeholder="Search unpaid entries…"
            ariaLabel="Search unpaid entries by employee"
          />
        </div>
      }
    >
      <div className="dk-strip ap-kpis" role="group" aria-label="Accounts payable summary">
        <button
          type="button"
          className="dk-tile dk-tile--blue ap-kpi-btn"
          onClick={focusEntries}
          aria-label={`${unpaidEntries} unpaid entries — show the list`}
        >
          <span className="dk-tile__dot" aria-hidden="true" />
          <span className="dk-tile__text">
            <span className="dk-tile__value">{unpaidEntries}</span>
            <span className="dk-tile__label">Unpaid Entries</span>
          </span>
          <span className="ap-kpi-btn__hint" aria-hidden="true">View list ↓</span>
        </button>
        <div className="dk-tile dk-tile--gold">
          <span className="dk-tile__dot" aria-hidden="true" />
          <span className="dk-tile__text">
            <span className="dk-tile__value">{money(payrollLiability)}</span>
            <span className="dk-tile__label">Total Liability</span>
          </span>
        </div>
      </div>

      <div className="dp-grid dp-grid--main-side ap-grid">
        <section ref={sectionRef} tabIndex={-1} className="ap-entries" aria-labelledby="ap-entries-title" data-testid="unpaid-entries">
          <div className="ap-entries__head">
            <h2 id="ap-entries-title" className="inv-section-title">Unpaid Entries</h2>
            <span className="dt-summary" aria-live="polite">
              {term ? `${matches.length} of ${unpaidRows.length}` : `${unpaidRows.length} ${unpaidRows.length === 1 ? "entry" : "entries"}`}
            </span>
          </div>
          {unpaidRows.length === 0 ? (
            <DeskEmpty title="No unpaid entries">Everything has been paid.</DeskEmpty>
          ) : matches.length === 0 ? (
            <DeskEmpty title="No unpaid entries match your search." />
          ) : (
            <>
              <DeskTable
                testId="unpaid-entries-table"
                caption="Unpaid payroll entries"
                columns={columns}
                rows={visible}
                rowKey={(row) => `${row.id || row.supabase_user_id || row.employee}-${visible.indexOf(row)}`}
              />
              {hiddenCount > 0 ? (
                <div className="ap-entries__more">
                  <button type="button" className="dt-btn" onClick={() => setExpanded(true)}>
                    View All {matches.length}
                  </button>
                </div>
              ) : expanded && !term && matches.length > INITIAL_VISIBLE ? (
                <div className="ap-entries__more">
                  <button type="button" className="dt-btn" onClick={() => setExpanded(false)}>
                    Show fewer
                  </button>
                </div>
              ) : null}
            </>
          )}
        </section>

        <aside className="ap-side" aria-label="Payroll liability">
          <DeskPanel title="Payroll Liability">
            {liabilityRows.length === 0 ? (
              <p className="dp-hint ap-side__empty">No payroll data.</p>
            ) : (
              <ul className="ap-liab">
                {liabilityRows.map((row) => (
                  <li key={row.name}>
                    <span>{row.name}</span>
                    <b>{money(row.amount)}</b>
                  </li>
                ))}
              </ul>
            )}
            <div className="ap-liab__total">
              <span>Total Liability</span>
              <strong>{money(payrollLiability)}</strong>
            </div>
          </DeskPanel>
        </aside>
      </div>
    </CrewPilotModuleShell>
  );
}
